import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Calculator, ClipboardList, Calendar, CheckSquare, ChevronLeft, Printer, Archive, Trash2, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getMyToolsData, deleteToolItem, GetMyToolsDataOutputType } from 'zite-endpoints-sdk';

type ToolsData = GetMyToolsDataOutputType;

export default function MyTools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ToolsData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!user) return;
    getMyToolsData({}).then(setData).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (type: 'gp' | 'shift' | 'event' | 'checklist', id: string, action: 'archive' | 'delete') => {
    try {
      await deleteToolItem({ type, id, action });
      toast.success(action === 'archive' ? 'Archived' : 'Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const active = data ? {
    gp: data.gpCalculations.filter(r => r.status !== 'Archived'),
    shift: data.shiftPreps.filter(r => r.status !== 'Archived'),
    event: data.eventSetups.filter(r => r.status !== 'Archived'),
    checklist: data.checklistCompletions,
  } : null;

  const total = active ? active.gp.length + active.shift.length + active.event.length + active.checklist.length : 0;

  const ActionButtons = ({ type, id }: { type: 'gp' | 'shift' | 'event' | 'checklist'; id: string }) => (
    <div className="flex gap-1 shrink-0">
      <Button variant="ghost" size="sm" title="Print" onClick={() => window.print()}><Printer className="w-3.5 h-3.5" /></Button>
      <Button variant="ghost" size="sm" title="Archive" onClick={() => handleDelete(type, id, 'archive')}><Archive className="w-3.5 h-3.5" /></Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(type, id, 'delete')} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tools" className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">My Tools & Documents</h1>
          <p className="text-sm text-muted-foreground">All your saved calculations, sheets and completed checklists</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'GP Calculations', count: active?.gp.length || 0, icon: Calculator, href: '/gp-calculator' },
              { label: 'Shift Preps', count: active?.shift.length || 0, icon: ClipboardList, href: '/shift-prep' },
              { label: 'Event Setups', count: active?.event.length || 0, icon: Calendar, href: '/event-setup' },
              { label: 'Checklists', count: active?.checklist.length || 0, icon: CheckSquare, href: '/checklists' },
            ].map(s => {
              const Icon = s.icon;
              return (
                <Link key={s.label} to={s.href}>
                  <Card className="p-4 text-center hover:border-primary/40 transition-colors cursor-pointer">
                    <Icon className="w-5 h-5 mx-auto mb-1 text-primary" />
                    <p className="text-2xl font-bold">{s.count}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </Card>
                </Link>
              );
            })}
          </div>

          {total === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground text-sm mb-4">You haven't saved anything yet. Use the tools to get started.</p>
              <Link to="/tools"><Button>Browse Tools</Button></Link>
            </Card>
          ) : (
            <Tabs defaultValue="all">
              <TabsList className="mb-4">
                <TabsTrigger value="all">All ({total})</TabsTrigger>
                <TabsTrigger value="gp">GP Calcs ({active?.gp.length || 0})</TabsTrigger>
                <TabsTrigger value="shift">Shift Preps ({active?.shift.length || 0})</TabsTrigger>
                <TabsTrigger value="event">Events ({active?.event.length || 0})</TabsTrigger>
                <TabsTrigger value="checklists">Checklists ({active?.checklist.length || 0})</TabsTrigger>
              </TabsList>

              {['all', 'gp'].includes('all') && (
                <TabsContent value="all">
                  <div className="space-y-2">
                    {active?.gp.map(r => (
                      <Card key={r.id} className="p-3 flex items-center gap-3">
                        <Calculator className="w-4 h-4 text-blue-500 shrink-0" />
                        <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.name}</p><p className="text-xs text-muted-foreground">{r.type} · {r.createdAt ? format(new Date(r.createdAt), 'dd MMM yy') : ''}</p></div>
                        <Badge variant="outline" className="text-xs shrink-0">{r.status}</Badge>
                        <Button variant="outline" size="sm" onClick={() => navigate('/gp-calculator')}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        <ActionButtons type="gp" id={r.id} />
                      </Card>
                    ))}
                    {active?.shift.map(r => (
                      <Card key={r.id} className="p-3 flex items-center gap-3">
                        <ClipboardList className="w-4 h-4 text-emerald-500 shrink-0" />
                        <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.shiftType} · {r.createdAt ? format(new Date(r.createdAt), 'dd MMM yy') : ''}</p></div>
                        <Badge variant={r.status === 'Final' ? 'default' : 'outline'} className="text-xs shrink-0">{r.status}</Badge>
                        <Button variant="outline" size="sm" onClick={() => navigate('/shift-prep')}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        <ActionButtons type="shift" id={r.id} />
                      </Card>
                    ))}
                    {active?.event.map(r => (
                      <Card key={r.id} className="p-3 flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-purple-500 shrink-0" />
                        <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.eventDate ? format(new Date(r.eventDate), 'dd MMM yy') : ''}</p></div>
                        <Badge variant={r.status === 'Final' ? 'default' : 'outline'} className="text-xs shrink-0">{r.status}</Badge>
                        <Button variant="outline" size="sm" onClick={() => navigate('/event-setup')}><ExternalLink className="w-3.5 h-3.5" /></Button>
                        <ActionButtons type="event" id={r.id} />
                      </Card>
                    ))}
                    {active?.checklist.map(r => (
                      <Card key={r.id} className="p-3 flex items-center gap-3">
                        <CheckSquare className="w-4 h-4 text-amber-500 shrink-0" />
                        <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.completionDate ? format(new Date(r.completionDate), 'dd MMM yy') : ''}</p></div>
                        <Badge variant={r.status === 'Completed' || r.status === 'Signed Off' ? 'default' : 'outline'} className="text-xs shrink-0">{r.status}</Badge>
                        <ActionButtons type="checklist" id={r.id} />
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              )}

              <TabsContent value="gp">
                <div className="space-y-2">{active?.gp.map(r => (<Card key={r.id} className="p-3 flex items-center gap-3"><Calculator className="w-4 h-4 text-blue-500 shrink-0" /><div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.name}</p><p className="text-xs text-muted-foreground">{r.type} · {r.notes}</p></div><Badge variant="outline" className="text-xs">{r.status}</Badge><ActionButtons type="gp" id={r.id} /></Card>))}</div>
              </TabsContent>
              <TabsContent value="shift">
                <div className="space-y-2">{active?.shift.map(r => (<Card key={r.id} className="p-3 flex items-center gap-3"><ClipboardList className="w-4 h-4 text-emerald-500 shrink-0" /><div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.shiftType}</p></div><Badge variant={r.status === 'Final' ? 'default' : 'outline'} className="text-xs">{r.status}</Badge><ActionButtons type="shift" id={r.id} /></Card>))}</div>
              </TabsContent>
              <TabsContent value="event">
                <div className="space-y-2">{active?.event.map(r => (<Card key={r.id} className="p-3 flex items-center gap-3"><Calendar className="w-4 h-4 text-purple-500 shrink-0" /><div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.eventDate ? format(new Date(r.eventDate), 'dd MMM yy') : ''}</p></div><Badge variant={r.status === 'Final' ? 'default' : 'outline'} className="text-xs">{r.status}</Badge><ActionButtons type="event" id={r.id} /></Card>))}</div>
              </TabsContent>
              <TabsContent value="checklists">
                <div className="space-y-2">{active?.checklist.map(r => (<Card key={r.id} className="p-3 flex items-center gap-3"><CheckSquare className="w-4 h-4 text-amber-500 shrink-0" /><div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{r.title}</p><p className="text-xs text-muted-foreground">{r.completionDate ? format(new Date(r.completionDate), 'dd MMM yy') : ''}</p></div><Badge variant={r.status === 'Completed' || r.status === 'Signed Off' ? 'default' : 'outline'} className="text-xs">{r.status}</Badge><ActionButtons type="checklist" id={r.id} /></Card>))}</div>
              </TabsContent>
            </Tabs>
          )}
        </>
      )}
    </div>
  );
}
